import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Chip,
} from '@heroui/react';
import {
  Typography,
  UniversalTable,
  CustomHeroButton,
  ResponsiveModal,
} from '../../../../components';
import { useTranslations } from '../../../../contexts/translation.context';
import { CalendarIcon, CheckArrowGreen, EmptyContentIc } from '../../../../components/icons';
import AddPublicLinkModal, { PublicLinkFormData } from './AddPublicLinkModal';
import partnerOfferService, { PublicLink } from '../../../../api/partner/partnerOfferService';

interface PublicLinksModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const PublicLinksModal: React.FC<PublicLinksModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslations();
  const [publicLinks, setPublicLinks] = useState<PublicLink[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [sortBy, setSortBy] = useState<string>('-created_at');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [editingLink, setEditingLink] = useState<PublicLink | null>(null);

  const fetchPublicLinks = async () => {
    try {
      setIsLoading(true);
      const response = await partnerOfferService.getPublicLinks({
        page: currentPage,
        page_size: pageSize,
        sort_by: sortBy,
      });
      setPublicLinks(response.objects || []);
      setTotalCount(response.count || 0);
    } catch (error) {
      console.error('Error fetching public links:', error);
      setPublicLinks([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchPublicLinks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, currentPage, pageSize, sortBy]);

  const handleAddLink = () => {
    setEditingLink(null);
    setIsAddModalOpen(true);
  };

  const handleEditLink = (link: PublicLink) => {
    setEditingLink(link);
    setIsAddModalOpen(true);
  };

  const handleSaveLink = async (data: PublicLinkFormData, linkId?: string) => {
    try {
      const requestData = {
        name: data.name,
        campaigns: (data.orderIds || []).map((id) => parseInt(id, 10)),
        income: data.showIncome,
        revenue: data.showRevenue,
        costs: data.showCosts,
        active: data.active,
      };

      if (linkId) {
        await partnerOfferService.updatePublicLink(linkId, requestData);
      } else {
        await partnerOfferService.createPublicLink(requestData);
      }

      // Refresh the list
      fetchPublicLinks();
      setIsAddModalOpen(false);
      setEditingLink(null);
    } catch (error) {
      console.error('Error saving public link:', error);
    }
  };

  const handleDeleteLink = async (linkId: string) => {
    try {
      await partnerOfferService.deletePublicLink(linkId);
      fetchPublicLinks();
    } catch (error) {
      console.error('Error deleting public link:', error);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatCreatedAt = useCallback((dateString?: string) => {
    if (!dateString) return '-';

    const utcDate = new Date(dateString);
    if (Number.isNaN(utcDate.getTime())) {
      return '-';
    }

    const localDate = new Date(utcDate.getTime() + utcDate.getTimezoneOffset() * -60000);

    const hours = localDate.getHours().toString().padStart(2, '0');
    const minutes = localDate.getMinutes().toString().padStart(2, '0');
    const day = localDate.getDate().toString().padStart(2, '0');
    const month = (localDate.getMonth() + 1).toString().padStart(2, '0');
    const year = localDate.getFullYear();

    return `${hours}:${minutes}, ${day}.${month}.${year}`;
  }, []);

  const columns = [
    {
      key: 'campaign',
      name: t('partner.orders.publicLinksModal.campaign'),
      sortable: true,
      sortKey: 'name',
    },
    {
      key: 'access',
      name: t('partner.orders.publicLinksModal.access'),
    },
    {
      key: 'status',
      name: t('partner.orders.publicLinksModal.status'),
      sortable: true,
      sortKey: 'created_at',
    },
    {
      key: 'actions',
      name: t('partner.orders.publicLinksModal.actions'),
    },
  ];

  const renderCell = useCallback(
    (link: PublicLink, columnKey: string) => {
      const publicUrl = `${window.location.origin}/partner/public/${link.id}`;

      switch (columnKey) {
        case 'campaign':
          return (
            <div className="flex flex-col gap-2">
              <Typography variant="label2" className="text-auth-ink">
                {link.name || 'Link ' + link.id.slice(0, 4)}
              </Typography>
              <Typography variant="body3" className="text-auth-muted">
                {link.campaigns.length > 0 ? link.campaigns.join(', ') : '-'}
              </Typography>
              <a
                href={publicUrl}
                target="__blank"
                className="flex items-center gap-1 bg-husky rounded-full px-2 py-1"
              >
                <Typography variant="body2" className="!text-partner truncate max-w-[18.75rem]">
                  {publicUrl}
                </Typography>
              </a>
            </div>
          );
        case 'access':
          const accessItems = [];
          if (link.revenue) accessItems.push(t('partner.orders.addPublicLinkModal.showRevenue'));
          if (link.income) accessItems.push(t('partner.orders.addPublicLinkModal.showIncome'));
          if (link.costs) accessItems.push(t('partner.orders.addPublicLinkModal.showCosts'));

          return (
            <div className="flex flex-col gap-1">
              {accessItems.map((item, index) => (
                <div key={index} className="flex items-center gap-2">
                  <CheckArrowGreen />
                  <Typography variant="body3" className="no-wrap text-auth-ink">
                    {item}
                  </Typography>
                </div>
              ))}
            </div>
          );
        case 'status':
          const formattedDate = formatCreatedAt(link.created_at);

          return (
            <div className="flex flex-col gap-2">
              {/* <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${link.active ? 'bg-success' : 'bg-auth-muted'}`} />
              <Typography variant="body3" className={link.active ? 'text-success' : 'text-auth-muted'}>
                {link.active ? t('partner.orders.addPublicLinkModal.active') : t('partner.orders.addPublicLinkModal.inactive')}
              </Typography>
            </div> */}
              <Chip
                className="rounded-lg bg-gray-100 text-gray-900"
                classNames={{
                  base: 'h-6 px-2 py-1 gap-1',
                }}
                size="sm"
                startContent={
                  <div
                    className={`w-2 h-2 rounded-full ${link.active ? 'bg-success' : 'bg-auth-muted'}`}
                  ></div>
                }
              >
                <Typography variant="body3" className="text-gray-900">
                  {link.active
                    ? t('partner.orders.addPublicLinkModal.active')
                    : t('partner.orders.addPublicLinkModal.inactive')}
                </Typography>
              </Chip>
              <div className="flex items-center gap-2">
                <CalendarIcon />
                <Typography variant="body3" className="text-auth-muted">
                  {formattedDate}
                </Typography>
              </div>
            </div>
          );
        case 'actions':
          return (
            <Button
              size="sm"
              className="bg-[#E6F1FE] text-partner h-8 px-3 rounded-2 text-sm font-normal"
              onPress={() => handleEditLink(link)}
            >
              {t('partner.orders.publicLinksModal.edit')}
            </Button>
          );
        default:
          return null;
      }
    },
    [formatCreatedAt, t]
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSort = (sortKey: string) => {
    setSortBy(sortKey);
    setCurrentPage(1);
  };

  return (
    <>
      <ResponsiveModal
        isOpen={isOpen}
        onClose={onClose}
        size="5xl"
        scrollBehavior="inside"
        classNames={{
          base: 'md:max-w-[59.375rem] max-h-[90vh]',
          body: 'p-0 overflow-y-auto',
          wrapper: 'items-center',
        }}
      >
        <ModalContent>
          <ModalHeader className="flex flex-col gap-3 px-4 md:px-6 pt-6 pb-4">
            <Typography variant="h6" className="text-auth-ink text-xl font-medium">
              {t('partner.orders.publicLinksModal.title')}
            </Typography>
            <Button
              className="bg-[#E6F1FE] text-partner h-10 px-3.5 py-2.5 rounded-3 w-fit text-sm font-normal"
              onPress={handleAddLink}
            >
              {t('partner.orders.publicLinksModal.addLink')}
            </Button>
          </ModalHeader>

          <ModalBody className="px-4 md:px-6 py-3">
            <UniversalTable
              data={publicLinks}
              columns={columns}
              renderCell={renderCell}
              emptyContent={
                <div className="text-center py-12">
                  <div className="flex flex-col items-center gap-6">
                    <EmptyContentIc />
                    <div className="flex flex-col gap-1.5">
                      <Typography
                        variant="h6"
                        className="text-xl font-semibold leading-7 text-auth-ink"
                      >
                        {t('partner.orders.publicLinksModal.emptyTitle')}
                      </Typography>
                    </div>
                  </div>
                </div>
              }
              pagination={{
                count: totalCount,
                page_size: pageSize,
                page: currentPage,
              }}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              currentPage={currentPage}
              currentPageSize={pageSize}
              sortBy={sortBy}
              onSort={handleSort}
              pageSizeOptions={[5, 10, 20]}
            />
          </ModalBody>

          <ModalFooter className="px-4 md:px-6 py-4">
            <CustomHeroButton
              className="bg-partner text-white h-10 px-3.5 rounded-3"
              onPress={onClose}
              title={t('partner.orders.publicLinksModal.close')}
            />
          </ModalFooter>
        </ModalContent>
      </ResponsiveModal>

      <AddPublicLinkModal
        isOpen={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          setEditingLink(null);
        }}
        onSave={handleSaveLink}
        editLink={editingLink}
        onDelete={handleDeleteLink}
      />
    </>
  );
};

export default PublicLinksModal;
